import { clientV3SDK, queryClient } from "@istari-frontend/api-client";
import { V3Models } from "@istari/istari-client";
import { infiniteQueryOptions, type QueryClient, queryOptions } from "@tanstack/react-query";

import { getFileRevisionContentByShaSalt } from "~/api/useModels";
import { DEFAULT_CACHE_TIME } from "~/constants";
import { ResourceSearchQuery } from "~/resource/data-source/resource-search-query";
import { ResourceType } from "~/resource/model/resource-type";

const LIST_PAGE_SIZE = 100;
// "Received via a secure connection": the v3 API has no is-not-null filter, so this negates the
// nil UUID — SQL `NOT IN` is false for NULL upstream_remote_id rows and true for any real remote
// id (the nil UUID never occurs as one). Replace with a dedicated filter once the registry has one.
const RECEIVED_VIA_REMOTE_FILTER = "!00000000-0000-0000-0000-000000000000";
// The v3 search endpoint caps size at 50; 25 keeps pages cheap for a type-ahead box.
const SEARCH_PAGE_SIZE = 25;

export interface ResourceBrowseOptions {
  includeTotal?: boolean;
  ownFilesFirst?: boolean;
}

export const resourceQueryKeys = {
  all: (pagination?: PaginationOptions) => ["resource", ...(pagination ? ["page", pagination] : [])],
  byId: (resourceId: string) => [...resourceQueryKeys.all(), resourceId],
  list: (resourceType: ResourceType, pagination?: PaginationOptions) => [
    ...resourceQueryKeys.all(pagination),
    "type",
    resourceType,
  ],
  listInfinite: (resourceTypes?: ResourceType[]) =>
    resourceTypes?.length
      ? [...resourceQueryKeys.all(), "types", ...sortedResourceTypes(resourceTypes), "infinite"]
      : [...resourceQueryKeys.all(), "list", "infinite"],
  searchInfinite: (resourceTypes: ResourceType[] | undefined, query?: ResourceSearchQuery) =>
    resourceTypes?.length
      ? [...resourceQueryKeys.all(), "types", ...sortedResourceTypes(resourceTypes), "search", query, "infinite"]
      : [...resourceQueryKeys.all(), "search", query, "infinite"],
  fullTextSearchInfinite: (searchTerm: string) => [
    ...resourceQueryKeys.all(),
    "fullTextSearch",
    searchTerm,
    "infinite",
  ],
  revision: (resourceId: string, revisionId: string) => [...resourceQueryKeys.byId(resourceId), "revision", revisionId],
  revisionList: (resourceId: string) => [...resourceQueryKeys.all(), resourceId, "revisions"],
  revisionContent: (resourceId: string, revisionId: string) => [
    ...resourceQueryKeys.revision(resourceId, revisionId),
    "content",
  ],
  exactRevisionContent: (resourceId: string, revisionId: string) => [
    ...resourceQueryKeys.revision(resourceId, revisionId),
    "exact-content",
  ],
  revisionRelationships: (revisionId: string, pagination?: PaginationOptions) => [
    "revision",
    revisionId,
    "relationships",
    ...(pagination ? [pagination] : []),
  ],
};

export type PaginationOptions = {
  cursor?: string;
  pageSize?: number;
};

/** The server's list order — created DESC with a resource-id tiebreak (its keyset). */
export function compareResourcesNewestFirst(
  a: Pick<V3Models.ResourceDto, "created" | "resourceId">,
  b: Pick<V3Models.ResourceDto, "created" | "resourceId">,
): number {
  const byCreated = new Date(b.created).getTime() - new Date(a.created).getTime();
  if (byCreated !== 0) return byCreated;
  return a.resourceId < b.resourceId ? -1 : a.resourceId > b.resourceId ? 1 : 0;
}

export const resourceQueryOptions = {
  metadataBatch: (resourceIds: string[], cache: QueryClient) => {
    const ids = [...new Set(resourceIds)].sort();
    return queryOptions({
      queryKey: [...resourceQueryKeys.all(), "metadata", ids],
      enabled: ids.length > 0,
      staleTime: DEFAULT_CACHE_TIME,
      // Key-based joins need every cursor page before they are complete.
      queryFn: async ({ signal }) => {
        if (!ids.length) return [];
        const resources: V3Models.ResourceDto[] = [];
        let cursor: string | undefined;
        do {
          const page = await clientV3SDK.listResources({
            resourceId: ids,
            archiveStatus: V3Models.ArchiveStatus.All,
            size: LIST_PAGE_SIZE,
            cursor,
          });
          signal.throwIfAborted();
          resources.push(...page.items);
          cursor = page.nextPage ?? undefined;
        } while (cursor);
        resources.forEach((resource) =>
          cache.setQueryData<V3Models.ResourceDto>(resourceQueryKeys.byId(resource.resourceId), resource),
        );
        return resources;
      },
    });
  },
  list: (resourceType?: ResourceType, pagination?: PaginationOptions) =>
    queryOptions({
      queryKey: resourceType ? resourceQueryKeys.list(resourceType, pagination) : resourceQueryKeys.all(pagination),
      queryFn: async () =>
        clientV3SDK.listResources({
          ...(resourceType && { typeName: [RESOURCE_TYPE_TO_API_RESOURCE_TYPE[resourceType]] }),
          cursor: pagination?.cursor,
          size: pagination?.pageSize ?? LIST_PAGE_SIZE,
        }),
      staleTime: DEFAULT_CACHE_TIME,
    }),
  listInfinite: (resourceTypes?: ResourceType[], browse?: ResourceBrowseOptions) => {
    const sortedTypes = sortedResourceTypes(resourceTypes);
    const typeName = toApiTypeNames(sortedTypes);
    const { includeTotal = false, ownFilesFirst = false } = browse ?? {};

    return infiniteQueryOptions({
      queryKey: [...resourceQueryKeys.listInfinite(resourceTypes), { includeTotal, ownFilesFirst }] as unknown[],
      queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
        clientV3SDK.listResources({
          ...(typeName && { typeName }),
          cursor: pageParam ?? undefined,
          size: LIST_PAGE_SIZE,
          includeTotal,
          ownFilesFirst,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      staleTime: DEFAULT_CACHE_TIME,
    });
  },
  searchInfinite: (resourceTypes?: ResourceType[], query?: ResourceSearchQuery) => {
    const sortedTypes = sortedResourceTypes(resourceTypes);
    const typeName = toApiTypeNames(sortedTypes);

    // Params shared by every request this search issues; text-term and cursor are added per call.
    // When `fileName` is set it is matched against name OR display name (see below), so the
    // separate `displayName` field is ignored in that mode — no caller sets both.
    const baseParams = {
      ...(typeName && { typeName }),
      ...(query?.resourceId?.length && { resourceId: query.resourceId }),
      ...(!query?.fileName && query?.displayName && { displayNameContains: [query.displayName] }),
      ...(query?.description && { descriptionContains: [query.description] }),
      ...(query?.versionName && { versionName: [query.versionName] }),
      ...(query?.mimeType?.length && { mimeType: query.mimeType }),
      ...(query?.externalIdentifier?.length && { externalIdentifier: query.externalIdentifier }),
      ...(query?.createdById?.length && { createdById: query.createdById }),
      ...(typeof query?.hasParents === "boolean" && { hasParents: query.hasParents }),
      ...(typeof query?.hasChildren === "boolean" && { hasChildren: query.hasChildren }),
      ...(query?.sharedViaScs && { upstreamRemoteId: [RECEIVED_VIA_REMOTE_FILTER] }),
      archiveStatus: query?.archiveStatus,
      size: LIST_PAGE_SIZE,
      includeTotal: true,
    };
    const fileNameTerm = query?.fileName;

    return infiniteQueryOptions({
      queryKey: resourceQueryKeys.searchInfinite(resourceTypes, query) as unknown[],
      // A file-name term must match the file name OR the display name — the UI displays
      // `displayName ?? name`, so users search for whichever label they see. The backend
      // ANDs distinct *_contains filters (a null display_name row can never satisfy both),
      // so the OR is built client-side: two identical requests differing only in the text
      // field probed, merged and deduped by resource id. The paired cursors ride in the
      // page cursor as JSON ({n, d}; null = that stream is exhausted). A backend
      // name-or-display-name filter would collapse this back into one request.
      queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
        if (!fileNameTerm) {
          return clientV3SDK.listResources({ ...baseParams, cursor: pageParam ?? undefined });
        }
        const cursors: { n?: string | null; d?: string | null } = pageParam
          ? (JSON.parse(pageParam) as { n?: string | null; d?: string | null })
          : {};
        const [namePage, displayPage] = await Promise.all([
          cursors.n === null
            ? null
            : clientV3SDK.listResources({
                ...baseParams,
                nameContains: [fileNameTerm],
                cursor: cursors.n ?? undefined,
              }),
          cursors.d === null
            ? null
            : clientV3SDK.listResources({
                ...baseParams,
                displayNameContains: [fileNameTerm],
                cursor: cursors.d ?? undefined,
              }),
        ]);
        // Each stream arrives in the server's order (created DESC, resource id tiebreak);
        // sort the merged page the same way. Global order across pages is restored at
        // render time by the search select (the two streams' pages can span different
        // time windows, so no per-page sort can guarantee it here).
        const mergedById = new Map<string, V3Models.ResourceDto>();
        const combined = [...(namePage?.items ?? []), ...(displayPage?.items ?? [])].sort(compareResourcesNewestFirst);
        for (const dto of combined) {
          if (!mergedById.has(dto.resourceId)) mergedById.set(dto.resourceId, dto);
        }
        const nameNext = namePage ? (namePage.nextPage ?? null) : null;
        const displayNext = displayPage ? (displayPage.nextPage ?? null) : null;
        return {
          items: [...mergedById.values()],
          // Upper bound: an item matching both terms is counted in each stream's total.
          total: (namePage?.total ?? 0) + (displayPage?.total ?? 0),
          nextPage:
            nameNext === null && displayNext === null ? undefined : JSON.stringify({ n: nameNext, d: displayNext }),
        } as V3Models.CursorPageResourceDto;
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      staleTime: DEFAULT_CACHE_TIME,
    });
  },
  // Ranked full-text search via GET /api/v3/search scoped to resources, hydrated through
  // listResources. The search index matches file name OR display name (plus description and
  // external identifier) in one ranked query — v3 *_contains filters can only AND across
  // fields, which drops rows whose display_name is null. Search hits carry only bare ids
  // (no type/file fields), so they are hydrated via the indexed, allowed-ids-cached
  // listResources path, which also narrows to models/artifacts server-side. (The v2
  // POST /resources/search endpoint was tried first but runs an uncached SpiceDB
  // LookupResources per request — far too slow.) Results reflect the search index
  // (~30s refresh cadence), so brand-new uploads can lag briefly; browse mode is live.
  fullTextSearchInfinite: (searchTerm: string) =>
    infiniteQueryOptions({
      queryKey: resourceQueryKeys.fullTextSearchInfinite(searchTerm) as unknown[],
      queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
        const searchPage = await clientV3SDK.search({
          q: searchTerm,
          entityType: V3Models.SearchEntityType.Resource,
          size: SEARCH_PAGE_SIZE,
          cursor: pageParam ?? undefined,
        });
        const rankedIds = searchPage.items.map((item) => item.id);
        if (rankedIds.length === 0) {
          return { items: [], nextPage: searchPage.nextCursor ?? undefined };
        }
        const hydrated = await clientV3SDK.listResources({
          resourceId: rankedIds,
          typeName: ["model", "artifact"],
          size: rankedIds.length,
        });
        const dtoById = new Map(hydrated.items.map((dto) => [dto.resourceId, dto]));
        return {
          // Preserve search rank order; ids hydration dropped (documents, etc.) fall out here.
          items: rankedIds.flatMap((id) => dtoById.get(id) ?? []),
          nextPage: searchPage.nextCursor ?? undefined,
        };
      },
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  search: (resourceType?: ResourceType, query?: ResourceSearchQuery, pagination?: PaginationOptions) =>
    queryOptions({
      queryKey: resourceType
        ? [...resourceQueryKeys.list(resourceType, pagination), "search", query]
        : [...resourceQueryKeys.all(pagination), "search", query],
      queryFn: async () =>
        clientV3SDK.listResources({
          ...(resourceType && { typeName: [RESOURCE_TYPE_TO_API_RESOURCE_TYPE[resourceType]] }),
          ...(query?.resourceId?.length && { resourceId: query.resourceId }),
          ...(query?.fileName && { nameContains: [query.fileName] }),
          ...(query?.displayName && { displayNameContains: [query.displayName] }),
          ...(query?.description && { descriptionContains: [query.description] }),
          ...(query?.versionName && { versionName: [query.versionName] }),
          ...(query?.mimeType?.length && { mimeType: query.mimeType }),
          ...(query?.externalIdentifier?.length && { externalIdentifier: query.externalIdentifier }),
          ...(query?.createdById?.length && { createdById: query.createdById }),
          ...(typeof query?.hasParents === "boolean" && { hasParents: query.hasParents }),
          ...(typeof query?.hasChildren === "boolean" && { hasChildren: query.hasChildren }),
          ...(query?.sharedViaScs && { upstreamRemoteId: [RECEIVED_VIA_REMOTE_FILTER] }),
          archiveStatus: query?.archiveStatus,
          cursor: pagination?.cursor,
          size: pagination?.pageSize ?? LIST_PAGE_SIZE,
          includeTotal: true,
        }),
      staleTime: DEFAULT_CACHE_TIME,
    }),
  byId: (resourceId: string) =>
    queryOptions({
      queryKey: resourceQueryKeys.byId(resourceId),
      queryFn: () => clientV3SDK.getResource({ resourceId }),
      enabled: !!resourceId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  revision: (resourceId: string, revisionId: string) =>
    queryOptions({
      queryKey: resourceQueryKeys.revision(resourceId, revisionId),
      queryFn: async () => {
        const [v3Dto, resourceDto] = await Promise.all([
          clientV3SDK.getResourceRevision({ resourceId, revisionId }),
          queryClient.ensureQueryData(resourceQueryOptions.byId(resourceId)),
        ]);
        return { ...v3Dto, hasChildren: resourceDto.hasChildren ?? false };
      },
      enabled: !!resourceId && !!revisionId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  revisionList: (resourceId: string) =>
    queryOptions({
      queryKey: resourceQueryKeys.revisionList(resourceId),
      queryFn: async () => clientV3SDK.listResourceRevisions({ resourceId }),
      enabled: !!resourceId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  // The resource's creation revision. The endpoint sorts created-descending and the SDK exposes
  // no ascending sort, so this drains pages (same eager-drain approach as
  // fetchAllRevisionRelationships) and keeps the final page's last item. Access-redacted
  // revisions are silently omitted server-side, so this is the oldest *visible* revision.
  // The default includeTotal: true is left in place — include_total=false fails CursorPage
  // response validation server-side (fastapi-pagination requires `total`), 500ing the request.
  oldestRevision: (resourceId: string) =>
    queryOptions({
      queryKey: [...resourceQueryKeys.byId(resourceId), "oldest-revision"],
      queryFn: async (): Promise<V3Models.ResourceRevisionDto | null> => {
        let cursor: string | undefined;
        let page: V3Models.CursorPageResourceRevisionDto;
        do {
          page = await clientV3SDK.listResourceRevisions({
            resourceId,
            cursor: cursor ?? undefined,
            size: 100,
          });
          cursor = page.nextPage ?? undefined;
        } while (cursor);
        return page.items.length > 0 ? page.items[page.items.length - 1] : null;
      },
      enabled: !!resourceId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  revisionRelationships: (revisionId: string, pagination?: PaginationOptions) =>
    queryOptions({
      queryKey: resourceQueryKeys.revisionRelationships(revisionId, pagination),
      queryFn: () =>
        clientV3SDK.listRevisionRelationships({
          revisionId,
          cursor: pagination?.cursor,
          size: pagination?.pageSize,
        }),
      staleTime: DEFAULT_CACHE_TIME,
    }),
  revisionRelationshipsInfinite: (revisionId: string) =>
    infiniteQueryOptions({
      queryKey: [...resourceQueryKeys.revisionRelationships(revisionId), "infinite"] as unknown[],
      queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
        clientV3SDK.listRevisionRelationships({
          revisionId,
          cursor: pageParam ?? undefined,
          size: 100,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
      enabled: !!revisionId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  revisionContent: (resourceId: string, revisionId: string) =>
    queryOptions({
      queryKey: resourceQueryKeys.revisionContent(resourceId, revisionId),
      queryFn: async () => {
        const revisionDto = await queryClient.ensureQueryData(resourceQueryOptions.revision(resourceId, revisionId));
        if (!revisionDto.contentToken) {
          throw new Error("Content token not found for revision. This should not happen. Please report this issue.");
        }
        const content = await getFileRevisionContentByShaSalt(
          revisionDto.contentToken.sha,
          revisionDto.contentToken.salt,
        );
        return {
          content,
          revision: revisionDto,
          resourceId,
        };
      },
      enabled: !!resourceId && !!revisionId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  // One revision's metadata and bytes, read without the resource lookup `revision` adds for its
  // `hasChildren`. A revision is immutable, so a read in flight can serve any concurrent caller;
  // `gcTime: 0` drops the bytes once no read holds them, since a bulk download would otherwise
  // pin every file it read. `reportsOwnErrors` skips the cache-level report, so every caller
  // must report this query's failures itself.
  exactRevisionContent: (resourceId: string, revisionId: string) =>
    queryOptions({
      queryKey: resourceQueryKeys.exactRevisionContent(resourceId, revisionId),
      queryFn: async () => {
        const revision = await clientV3SDK.getResourceRevision({ resourceId, revisionId });
        const content = revision.contentToken
          ? await getFileRevisionContentByShaSalt(revision.contentToken.sha, revision.contentToken.salt)
          : null;
        return { revision, content };
      },
      staleTime: DEFAULT_CACHE_TIME,
      gcTime: 0,
      meta: { reportsOwnErrors: true },
    }),
};

const RESOURCE_TYPE_TO_API_RESOURCE_TYPE: Record<ResourceType, string> = {
  [ResourceType.Model]: "model",
  [ResourceType.Artifact]: "artifact",
  [ResourceType.File]: "file",
  [ResourceType.ConnectedResource]: "connected_resource",
  [ResourceType.ConnectedContainer]: "connected_container",
};

function sortedResourceTypes(types: ResourceType[] | undefined): ResourceType[] {
  if (!types?.length) return [];
  return [...types].sort();
}

function toApiTypeNames(types: ResourceType[]): string[] | undefined {
  if (!types.length) return undefined;
  return types.map((type) => RESOURCE_TYPE_TO_API_RESOURCE_TYPE[type]);
}
